import { Log, UserManager } from 'oidc-client-ts';

import { config } from '@/config';
import { routeStorage } from './storage';

// True only when this window IS the hidden silent-renew iframe spawned by our own
// UserManager (parent has the same origin and can therefore receive a postMessage
// targeted at our origin). When this microfrontend is embedded inside a different
// origin (e.g. manservicecare.rio.cloud), accessing `window.parent.location.origin`
// throws a SecurityError, so we treat it as "not a silent-renew iframe" and fall back
// to the regular redirect callback flow.
const isSilentRenewIframe = (): boolean => {
    try {
        const hasParent = window?.parent && window.parent !== window;
        if (!hasParent) {
            return false;
        }
        return window.parent.location.origin === window.location.origin;
    } catch {
        return false;
    }
};

const buildUserManager = () =>
    new UserManager({
        authority: `${config.login.authority}`,
        client_id: `${config.login.clientId}`,
        redirect_uri: `${config.login.redirectUri}`,
    });

const completeRedirectCallback = async () => {
    try {
        await buildUserManager().signinRedirectCallback(window.location.href);
    } catch (error) {
        // Even if consuming the code fails, we must not leave the iframe stuck on the
        // /redirect HTML — the parent shell is waiting for the application to mount.
        console.warn('[configuration/login/redirect] signinRedirectCallback failed', error);
    }

    const initialRoute = routeStorage.getRoute() ?? '/';
    routeStorage.discardRoute();
    window.location.replace(`${window.location.origin}${initialRoute}`);
};

export const handleLoginRedirect = async () => {
    if (isSilentRenewIframe()) {
        // Silent redirect within an <iframe> of our own origin.
        // This will propagate the received information provided via query parameters
        // to the parent frame using postMessage with our own origin as targetOrigin.
        Log.setLogger(console);
        Log.setLevel(Log.INFO);
        await buildUserManager().signinSilentCallback();
        return;
    }

    // Either we are the top-level window after a fresh login, or we are this
    // microfrontend embedded as an iframe inside a cross-origin parent shell.
    // In both cases, consume the authorization code with `signinRedirectCallback`
    // and navigate back into the application so React can mount.
    await completeRedirectCallback();
};
